import { useMemo } from 'react'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useSearchAvailableAreaCodes } from '@forwardflow/queries/onboarding.queries'
import { DEV_DUMMY, isFreeFlow, isSandbox } from '../../dev/devTools'
import { areaCodeOf } from '../../shell/format'

export const MAX_AREA_CODES = 3

/**
 * The area codes a customer can pick from: their team's phones, checked
 * for availability. Shared by the setup step and the campaign composer.
 */
export const useAreaCodeSuggestions = () => {
   const { onboardingData } = useOnboardingStoreV2()
   const freeFlow = isFreeFlow()
   const teamCodes = useMemo(() => {
      const users = [...(onboardingData.invitedUsers || [])].sort(
         (a, b) => Number(b.isOwner) - Number(a.isOwner)
      )
      const seen = new Set<string>()
      const out: { code: string; name: string; isOwner: boolean }[] = []
      users.forEach((u) => {
         const code = areaCodeOf(u.phone)
         if (!code || seen.has(code)) return
         seen.add(code)
         out.push({
            code,
            name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
            isOwner: Boolean(u.isOwner),
         })
      })
      return freeFlow && out.length === 0
         ? DEV_DUMMY.areaCodes.map((code, i) => ({
              code,
              name: i === 0 ? 'you' : 'your team',
              isOwner: i === 0,
           }))
         : out.slice(0, MAX_AREA_CODES)
   }, [onboardingData.invitedUsers, freeFlow])

   // Availability checks run against the backend only outside the sandbox.
   const q = (i: number) => (isSandbox() ? '' : teamCodes[i]?.code || '')
   const check0 = useSearchAvailableAreaCodes(q(0))
   const check1 = useSearchAvailableAreaCodes(q(1))
   const check2 = useSearchAvailableAreaCodes(q(2))
   const checks = [check0, check1, check2]
   const suggestions = teamCodes.map((t, i) => {
      const list: number[] | undefined = checks[i].data?.areaCodes
      const available = freeFlow
         ? true
         : Array.isArray(list)
           ? list.map(String).includes(t.code)
           : undefined
      return { ...t, available, isChecking: checks[i].isLoading }
   })
   const isSuggesting = suggestions.some((s) => s.isChecking)
   const ready = suggestions.filter((s) => s.available === true).map((s) => s.code)
   const ownerCode = suggestions.find((s) => s.isOwner)?.code
   return { suggestions, isSuggesting, ready, ownerCode }
}
